import {
  BallFinalMetricsConfiguratorV3,
  BallFinalMetricsViewerV3,
  BfMetricTileSize,
  Dexterity,
  IBallFinal,
  IBfMetricListItem,
  IStrikeZoneDecision,
  Spin3DFinal,
  StrikeZoneDefinitionEnum,
  UnitSystem,
  metersToInches,
} from '@trackman/baseball-shared-components';
import { IStrikeZonePoint } from '@trackman/baseball-shared-components/build/models/broadcast/IStrikeZonePoint';
import LDLoader from 'components/loader/LDLoader';
import { Spin3dTiles } from 'constants/Spin3dTiles';
import { History } from 'history';
import { useStores } from 'index';
import { IPlayData } from 'interfaces/broadcast/IPlayData';
import { Decision, IStrikeZoneDecisionModel } from 'interfaces/session/IStrikeZoneDecision';
import { StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { uniqueId } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { ListView } from './ListView/ListView';
import { MetricsTopBar } from './MetricsTopBar/MetricsTopBar';
import { StrikezoneView } from './StrikezoneView/StrikezoneView';
import { MetricsPage } from './styles/MetricsStyles';

interface IProps {
  match?: any;
  location?: any;
  history?: History;
}

export enum LiveDashboardView {
  Dashboard = 'Dashboard',
  Strikezone = 'Strikezone',
  List = 'List',
}

export const Metrics: React.FC<IProps> = observer((props) => {
  const feedService = useStores().broadcastService;
  const liveDashboardStore = useStores().liveDashboardStore;
  const uiStore = useStores().uiStore;
  const { id } = props.match.params;
  const { metrics, tileSize, unitSystem } = liveDashboardStore;
  const [shouldUpdateList, setShouldUpdateList] = useState<string>();

  const gameStore = useStores().gameStore;
  const logService = useStores().logService;
  const permissionStore = useStores().permissionStore;
  const [isLoading, setIsLoading] = useState(true);
  const displayDataStore = useStores().displayDataStore;
  const [useStrikeZoneDecision, setUseStrikeZoneDecision] = useState(false);

  const formatValue = (value: number, isImperial: boolean) => (isImperial ? metersToInches(value) : (value * 100).toFixed(2));

  useEffect(() => {
    const lastDisplayPlay =
      displayDataStore.displayData && displayDataStore.displayData.length > 0 ? displayDataStore.displayData[0] : null;
    const isImperial = liveDashboardStore.unitSystem === UnitSystem.Imperial;

    if (lastDisplayPlay?.strikeZoneDecision) {
      const { playerAtBat, strikeZoneType, dimensions } = lastDisplayPlay.strikeZoneDecision;
      const { top, bottom, left, right, ballRadius } = dimensions;

      const data = getStrikeZoneDecision(lastDisplayPlay.strikeZoneDecision as IStrikeZoneDecisionModel);

      let config: any;

      if (data) {
        config = {
          strikeZoneType: lastDisplayPlay.strikeZoneDecision.strikeZone.customDefinition
            ? lastDisplayPlay.strikeZoneDecision.strikeZone.customDefinition.activeStrikeZone
            : lastDisplayPlay.strikeZoneDecision.strikeZone.activeStrikeZone,
          top: data.dimensions.top,
          bottom: data.dimensions.bottom,
          left: data.dimensions.left,
          right: data.dimensions.right,
          ballRadius: data.dimensions.ballRadius,
          topStrikeZoneValue: formatValue(data.dimensions.top, isImperial),
          bottomStrikeZoneValue: formatValue(data.dimensions.bottom, isImperial),
        };
      } else {
        config = {
          strikeZoneType,
          top,
          bottom,
          left,
          right,
          ballRadius,
          topStrikeZoneValue: formatValue(top, isImperial),
          bottomStrikeZoneValue: formatValue(bottom, isImperial),
        };
      }

      return liveDashboardStore.setStrikeZoneConfig({
        batterHandedness: playerAtBat?.battingHandedness ?? Dexterity.Right,
        name: '',
        ...config,
      });
    }

    if (gameStore.game) {
      const values = gameStore.game.strikeZoneDefinition;

      if (values) {
        const inchesToMeters = (value: number) => {
          return value * 0.0254;
        };

        return liveDashboardStore.setStrikeZoneConfig({
          name: '',
          left: values.staticStrikeZoneWidth ? -inchesToMeters(values.staticStrikeZoneWidth / 2) : 0.037338,
          right: values.staticStrikeZoneWidth ? inchesToMeters(values.staticStrikeZoneWidth / 2) : 0.501904,
          top: values.staticStrikeZoneTop ? inchesToMeters(values.staticStrikeZoneTop) : -0.2159,
          bottom: values.staticStrikeZoneBottom ? inchesToMeters(values.staticStrikeZoneBottom) : 0.2159,
          ballRadius: values.ballDiameter ? inchesToMeters(values.ballDiameter / 2) : 1.082294,
          batterHandedness: Dexterity.Right,
          strikeZoneType: values.activeStrikeZone,
          topStrikeZoneValue: formatValue(inchesToMeters(values.staticStrikeZoneTop), isImperial),
          bottomStrikeZoneValue: formatValue(inchesToMeters(values.staticStrikeZoneBottom), isImperial),
        });
      }
    }
  }, [displayDataStore.ballFinals, displayDataStore.playData, liveDashboardStore.unitSystem, displayDataStore.displayData]);

  const getPermissions = () => {
    if (gameStore.game) {
      permissionStore
        .setPermissions({
          id: gameStore.game.id,
          gameDate: gameStore.game.gameDate,
          leagueId: gameStore.game.leagueId,
          levelId: gameStore.game.levelId,
          fieldId: gameStore.game.fieldId,
          venueId: gameStore.game.venueId,
          homeTeamId: gameStore.game.homeTeamId,
        })
        .then(() => {
          if (!permissionStore.permissions.isSpin3dAllowed) {
            Spin3dTiles.forEach((tile) => {
              const spin3dTile = metrics.find((item) => item.type === tile);

              if (spin3dTile) {
                spin3dTile.isEnabled = false;
              }
            });
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  };

  const fetchData = async () => {
    try {
      if (!gameStore.game) {
        const params = new URLSearchParams(props.location.search);

        if (params.get('isScheduled') === null) {
          return props.history.push('/');
        }

        await gameStore.setGameById(id, isScheduled, useSharingNetworkRules).then(() => {
          getPermissions();
        });
      } else {
        getPermissions();
      }

      gameStore.calculateHidePitchLocation().then(() => {
        liveDashboardStore.initializeMetricsFromLocalStorage(gameStore.hidePitchLocation);
      });
    } catch (error) {
      logService.logError({ error, message: `When getting game metadata.` });
    }
  };

  useEffect(() => {
    fetchData().then(() => {
      feedService.watch(gameStore.game.id, useSharingNetworkRules, onBallFinal, onSpin3dFinal, onPlayData, onStrikeZoneDecision);
    });
    return () => {
      feedService.stop();
      gameStore.clearGame();
      displayDataStore.clearGame();
      setUseStrikeZoneDecision(false);
    };
  }, []);

  const getStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecisionModel): Decision | undefined => {
    if (!strikeZoneDecision) {
      return undefined;
    }

    const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];

    const data = strikeZoneDecision.strikeZoneDecisionLocations.find((location) => priorityOrder.includes(location.strikeZoneLocationType));

    return data ? data : strikeZoneDecision;
  };

  const getAllStrikeZonePoints = () => {
    return displayDataStore.displayData.map((displayData) => {
      const priorityOrder = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];
      const data = displayData.strikeZoneDecision?.strikeZoneDecisionLocations.find((location) =>
        priorityOrder.includes(location.strikeZoneLocationType as StrikeZoneLocation)
      );
      return {
        index: displayData.index,
        ballX: data?.ballX || displayData.ballX,
        ballY: data?.ballY || displayData.ballY,
      };
    });
  };

  const getLastDisplayData = () => {
    if (displayDataStore.displayData.length > 0) {
      const lastDisplayData = displayDataStore.displayData[0];

      return lastDisplayData;
    }

    return undefined;
  };

  const getStrikeZonePointFromDisplayData = (): IStrikeZonePoint | undefined => {
    if (displayDataStore.displayData.length > 0) {
      const lastDisplayData = displayDataStore.displayData[0];
      const strikeZoneDecision = getStrikeZoneDecision(lastDisplayData.strikeZoneDecision as IStrikeZoneDecisionModel);

      return {
        ballX: strikeZoneDecision?.ballX || lastDisplayData.ballX,
        ballY: strikeZoneDecision?.ballY || lastDisplayData.ballY,
        isInZone: false,
      };
    }

    return undefined;
  };

  const useSharingNetworkRules = props.location.search.includes('sharingNetworkRules=true');
  const isScheduled = props.location.search.includes('isScheduled=true');

  const onBallFinal = (ballFinal: IBallFinal): void => {
    if (ballFinal.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(ballFinal.playId, [ballFinal], undefined, undefined, undefined);
    } else {
      displayDataStore.setBallFinals(ballFinal);
    }
  };

  const onSpin3dFinal = (spin3dFinal: Spin3DFinal): void => {
    if (spin3dFinal.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(undefined, [], spin3dFinal, undefined, undefined);
    } else {
      displayDataStore.setSpin3dFinal(spin3dFinal);
    }
  };

  const onPlayData = (playData: IPlayData): void => {
    if (playData.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(undefined, [], undefined, undefined, playData);
    } else {
      displayDataStore.setPlayData(playData);
    }
  };

  const onStrikeZoneDecision = (strikeZoneDecision: IStrikeZoneDecision): void => {
    setUseStrikeZoneDecision(true);
    let szDecision = strikeZoneDecision;
    if (szDecision.strikeZoneDecisionLocations === null || szDecision.strikeZoneDecisionLocations.length === 0) {
      // Backward compatibility for older systems: fallback to front of homeplate
      szDecision.strikeZoneDecisionLocations = [
        {
          ballX: szDecision.ballX,
          ballY: szDecision.ballY,
          dimensions: szDecision.dimensions,
          ballContactArea: szDecision.isInZone ? 'Inside' : 'Outside',
          strikeZoneLocationType: 'Front',
        },
      ];
    }
    if (strikeZoneDecision.playId !== displayDataStore.currentPlayId) {
      displayDataStore.handleNewPlay(strikeZoneDecision.playId, [], undefined, strikeZoneDecision, undefined);
    } else {
      displayDataStore.setLastStrikeZoneDecision(strikeZoneDecision);
    }
  };

  useEffect(() => {
    displayDataStore.handleNewEventMessage();
  }, [displayDataStore.ballFinals, displayDataStore.spin3dFinal, displayDataStore.lastStrikeZoneDecision, displayDataStore.playData]);

  useEffect(() => {
    document.querySelector('body')?.setAttribute('data-settings', liveDashboardStore.isSettingsMenuVisible.toString());
  }, [liveDashboardStore.isSettingsMenuVisible]);

  const onConfigChangeHandler = (newMetrics: IBfMetricListItem[], unitSystem: UnitSystem, tileSize: BfMetricTileSize) => {
    liveDashboardStore.setMetrics(newMetrics);

    setShouldUpdateList(uniqueId());
    liveDashboardStore.setTileSize(tileSize);
    liveDashboardStore.setUnitSystem(unitSystem);
  };

  const getPlayerName = (playerName?: string) => {
    if (playerName && playerName.includes(', ')) {
      const [secondName, firstName] = playerName.split(', ');
      return `${firstName} ${secondName}`;
    }

    return playerName;
  };

  const getPlates = () => {
    if (useStrikeZoneDecision) {
      return (
        getLastDisplayData()?.strikeZoneDecision?.strikeZoneDecisionLocations?.map((location, idx) => {
          return {
            id: idx,
            strikeZoneCalculationType: location.strikeZoneLocationType,
            offset:
              getLastDisplayData()?.strikeZoneDecision?.strikeZone?.locations?.find(
                (x) => x.locationType === location.strikeZoneLocationType
              )?.offsetY || 0,
            extension:
              getLastDisplayData()?.strikeZoneDecision?.strikeZone?.locations?.find(
                (x) => x.locationType === location.strikeZoneLocationType
              )?.bottomExtension || 0,
          };
        }) || []
      );
    }

    // Fallback to front of homeplate from BallFinal
    return [
      {
        id: 0,
        strikeZoneCalculationType: 'Front',
        offset: 0,
        extension: 0,
      },
    ];
  };

  const getBallPositions = (plate: 'Front' | 'Middle' | 'Back') => {
    const positions = getLastDisplayData()?.strikeZoneDecision?.strikeZoneDecisionLocations.find((x) => x.strikeZoneLocationType === plate);

    if (positions) {
      return {
        ballX: positions.ballX,
        ballY: positions.ballY,
        ballContactArea: positions.ballContactArea,
      };
    }

    return undefined;
  };

  const getFrontBallPosition = () => {
    if (useStrikeZoneDecision) {
      return getBallPositions('Front');
    }

    // Fallback to front of homeplate from BallFinal
    const lastDisplayData = getLastDisplayData();

    if (lastDisplayData) {
      return {
        ballX: lastDisplayData.ballX,
        ballY: lastDisplayData.ballY,
        ballContactArea: lastDisplayData.strikeZoneDecision?.isInZone ? 'Inside' : 'Outside',
      };
    }

    return undefined;
  };

  return (
    <div>
      {isLoading ? (
        <LDLoader size='large' style={{ height: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
      ) : (
        <>
          <MetricsPage>
            <MetricsTopBar />
            <div className='container'>
              <BallFinalMetricsConfiguratorV3
                metrics={metrics}
                tileSize={tileSize}
                unitSystem={liveDashboardStore.unitSystem}
                onConfigChanged={onConfigChangeHandler}
                onHide={(): void => liveDashboardStore.setIsSettingsMenuVisible(false)}
                isVisible={liveDashboardStore.isSettingsMenuVisible}
                darkMode={liveDashboardStore.darkMode}
                width='300px'
                disableStrikeZoneDecision
                shouldUpdateList={shouldUpdateList}
              />
              {uiStore.currentView === LiveDashboardView.Dashboard && (
                <BallFinalMetricsViewerV3
                  metrics={metrics.filter((x) => x.isChecked)}
                  tileSize={tileSize}
                  unitSystem={unitSystem}
                  ballFinals={displayDataStore.ballFinals}
                  strikeZonePoint={getStrikeZonePointFromDisplayData()}
                  disableStrikeZoneDecision
                  darkMode={liveDashboardStore.darkMode}
                  strikeZoneConfig={liveDashboardStore.strikezoneConfig}
                  spin3dFinal={displayDataStore.spin3dFinal}
                  playerName={getPlayerName(displayDataStore.playData?.batter.name)}
                  strikeZoneCalculationInformations={getPlates()}
                  strikeZonePoints={{
                    front: getFrontBallPosition(),
                    middle: getBallPositions('Middle'),
                    back: getBallPositions('Back'),
                  }}
                />
              )}
              {uiStore.currentView === LiveDashboardView.Strikezone && (
                <StrikezoneView
                  data={getAllStrikeZonePoints()}
                  darkMode={liveDashboardStore.darkMode}
                  config={liveDashboardStore.strikezoneConfig}
                />
              )}
              {uiStore.currentView === LiveDashboardView.List && (
                <ListView
                  data={displayDataStore.displayData}
                  metrics={metrics.filter((x) => x.isChecked)}
                  unitSystem={unitSystem}
                  itemCounter={displayDataStore.itemCounter}
                />
              )}
            </div>
          </MetricsPage>
        </>
      )}
    </div>
  );
});
